
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

// Ensure this Group interface is consistent across all files
export interface Group {
  id: string;
  name: string;
  description: string | null;
  sort_order: number; // Not optional
}

interface GroupDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  currentGroup: Group | null;
  setCurrentGroup: (group: Group | null) => void;
  onSave: () => Promise<void>;
  isSubmitting?: boolean;
}

const GroupDialog: React.FC<GroupDialogProps> = ({
  isOpen,
  setIsOpen,
  currentGroup,
  setCurrentGroup,
  onSave,
  isSubmitting = false,
}) => {
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (currentGroup) {
      setCurrentGroup({
        ...currentGroup,
        name: e.target.value
      });
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (currentGroup) {
      setCurrentGroup({
        ...currentGroup,
        description: e.target.value
      });
    }
  };

  const handleSortOrderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (currentGroup) {
      setCurrentGroup({
        ...currentGroup,
        sort_order: parseInt(e.target.value) || 0
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {currentGroup && currentGroup.id ? 'Edit Group' : 'Add Group'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={currentGroup?.name || ''}
              onChange={handleNameChange}
              disabled={isSubmitting}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={currentGroup?.description || ''}
              onChange={handleDescriptionChange}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sort_order">Sort Order</Label>
            <Input
              id="sort_order"
              type="number"
              min="0"
              step="1"
              value={currentGroup?.sort_order ?? 0}
              onChange={handleSortOrderChange}
              disabled={isSubmitting}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GroupDialog;
